import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { showMutationError } from "utils/showMutationError";

const isHealthLinkVaccine = (vaccineType, vaccineCodeId) => {
  if (!vaccineCodeId) return false;

  if (!vaccineType || typeof vaccineType !== "string") return false;

  const typeLower = vaccineType.toLowerCase();
  const isCovid = typeLower.startsWith("covid-19");
  const isInfluenza = vaccineType === "Influenza";
  const isPneumococcal =
    vaccineType === "Pneumococcal" || vaccineType === "Pneumococcol";

  return isCovid || isInfluenza || isPneumococcal;
};

/*
 * The mitigable validations behind a 422.
 *
 * Today the immunisation controllers answer 422 with the whole DTO, so they sit at the
 * top level. That controller is not registered with `ApiExceptionHandler` yet; when it
 * is, the body becomes an `ApiResponse` envelope and the DTO moves under `data`. Reading
 * both means the warnings dialog — the whole mitigate-and-continue flow — does not go
 * silently dead the day that switch is flipped: `validations` is a bespoke field the
 * envelope has no place for, so nothing else would have caught it.
 */
const getValidations = (error) => {
  const body = error?.response?.data;

  return body?.validations ?? body?.data?.validations;
};

export const useVaccineFormMutation = ({
  tenantVal,
  vaccineId,
  onSuccess,
  onValidationError,
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { skipGlobalError: true },
    onError: (error, variables) => {
      const status = error?.response?.status;
      const validations = getValidations(error);

      if (
        status === 422 &&
        Array.isArray(validations) &&
        validations.length > 0 &&
        typeof onValidationError === "function"
      ) {
        onValidationError({
          validations,
          serverPayload: error?.response?.data,
          submitPayload: variables,
        });
        return;
      }

      showMutationError(error);
      queryClient.invalidateQueries({ queryKey: ["vaccines"] });
      queryClient.invalidateQueries({ queryKey: ["allImmunisations"] });
      queryClient.invalidateQueries({ queryKey: ["healthlinkImmunisations"] });
    },
    mutationFn: async (values) => {
      const axiosInstance = createAxiosInstance();
      const isManual = !values.vaccineCodeId;
      const isFluOrPneumo =
        values.type === "Influenza" ||
        values.type === "Pneumococcal" ||
        values.type === "Pneumococcol";
      const isDose1 = values.doseNumberCode === "1";
      // Dose 1 → Course Completion (X0070-1/2);
      // Dose 2+ → Clinical Necessity (X0070-3/4 + clinicallyNecessary flag).
      const isFluOrPneumoDose1 = isFluOrPneumo && isDose1;
      const isFluOrPneumoDose2Plus =
        isFluOrPneumo && !!values.doseNumberCode && !isDose1;

      const courseCompletionCode = isFluOrPneumoDose1
        ? values.administrationNotesCode || null
        : null;

      const clinicalNecessityCode =
        isFluOrPneumoDose2Plus && values.clinicallyNecessary === true
          ? "X0070-3"
          : isFluOrPneumoDose2Plus && values.clinicallyNecessary === false
          ? "X0070-4"
          : null;

      // BE builds the HL7 RXA.9 from the single administrationNotesCode, so
      // send the course-completion code for dose 1 and the clinical-necessity
      // code for dose 2+ (the array is kept for the newer BE contract).
      const administrationNotesCode = isFluOrPneumo
        ? courseCompletionCode || clinicalNecessityCode
        : values.administrationNotesCode || null;

      const administrationNotesCodes = [
        courseCompletionCode,
        clinicalNecessityCode,
      ].filter(Boolean);

      await axiosInstance.post(`${tenantVal}/patientimmunisation`, {
        id: vaccineId,
        // Trimmed, because the schema now asks for a non-blank name and the server's
        // @VaccineIdentified agrees: " Fluvax " would otherwise be stored with its
        // spaces and read back as a different vaccine than "Fluvax".
        vaccineName: values.vaccineName?.trim(),
        batchNo: values.batchNo,
        /*
         * The registry batch the dose was given from, when one was picked (KIN-1740).
         * Null for a typed number, which is what keeps manual entry working exactly as
         * before. The server copies the batch number and expiry from the batch itself
         * whatever this request says, and refuses a batch of a different product, so
         * sending the id is what makes the two agree rather than merely look alike.
         */
        vaccineBatchId: values.vaccineBatchId
          ? parseInt(values.vaccineBatchId)
          : null,
        administrationMethod: values.administrationMethod,
        dateGiven: values.dateGiven,
        expiryDate: values.expiryDate,
        siteGiven: values.siteGiven,
        doseMl: values.dose,
        reaction: values.reaction,
        reactionDate: values.reactionDate,
        // Mandatory for a catalogue immunisation and rejected if blank; a manual entry
        // has no product and is exempt, so null is correct there rather than a guess.
        specVersion: values.specVersion || null,
        notes: values.notes,
        patientId: values.patientId,
        type: values.type,
        // When resubmitting after 422, backend treats presence of validations as mitigated.
        ...(Array.isArray(values.validations) && values.validations.length > 0
          ? { validations: values.validations }
          : {}),
        ...(values.type === "PCI" && values.pciVaccineType
          ? { pciVaccineType: values.pciVaccineType }
          : {}),
        vaccineCodeId: values.vaccineCodeId
          ? parseInt(values.vaccineCodeId)
          : null,
        vaccineCodeName: values.vaccineCodeName || null,
        vaccineCodeCvxCode:
          values.vaccineCodeCvxCode || values.vaccineCodeSnomed || null,
        vaccineCodeMvxCode: values.vaccineCodeMvxCode || null,
        vaccineManufacturer: values.vaccineManufacturer || null,
        ...(administrationNotesCode ? { administrationNotesCode } : {}),
        administrationNotesCodes:
          administrationNotesCodes.length > 0 ? administrationNotesCodes : null,
        ...(isManual
          ? {}
          : {
              doseNumberCode: values.doseNumberCode || null,
              // Sent for whatever the risk-factor response offered these for, not for
              // COVID alone: the form only ever holds codes the server listed, and it
              // clears them when the product changes.
              additionalFactors: values.additionalFactors || [],
              immunocompromisedAssessment: values.immunocompromisedAssessment,
            }),
        ...(isFluOrPneumoDose2Plus
          ? { clinicallyNecessary: values.clinicallyNecessary ?? false }
          : {}),
        // null is meaningful and not the same as false: it is "not answered", which the
        // backend distinguishes because a false makes an Indication mandatory while a
        // true waives one. Never coerce it (KIN-1695).
        healthcareWorker: values.healthcareWorker ?? null,
        consentGivenCode: values.consentGivenCode || null,
        /*
         * A dose this practice did not give (KIN-1883). The timestamp being non-null is
         * the marker the backend keys its guarantees off — never submitted, never
         * claimed, but still fills its schedule slot. Sent explicitly because this
         * payload is assembled field by field, so anything absent here never reaches the
         * wire however carefully the form assembled it.
         */
        administeredElsewhereAt: values.administeredElsewhereAt ?? null,
        administeredElsewhereBy: values.administeredElsewhereBy ?? null,
        ambulatoryStatus: values.ambulatoryStatus || null,
        riskFactorCodes: values.riskFactorCodes || [],
        administrationStaffId: values.vaccinator
          ? parseInt(values.vaccinator)
          : null,
      });
    },
    onSuccess: (data, variables) => {
      let message;
      if (vaccineId) {
        message = "Vaccine has been updated.";
      } else {
        const vaccineCodeId = variables.vaccineCodeId
          ? parseInt(variables.vaccineCodeId)
          : null;
        const isHealthLink = isHealthLinkVaccine(variables.type, vaccineCodeId);
        if (isHealthLink) {
          message = "Immunisation report has been sent to HealthLink.";
        } else {
          message = "Immunisation report has been added to the system.";
        }
      }

      toast.success(message);
      queryClient.invalidateQueries({ queryKey: ["vaccines"] });
      queryClient.invalidateQueries({ queryKey: ["allImmunisations"] });
      queryClient.invalidateQueries({ queryKey: ["healthlinkImmunisations"] });

      setTimeout(() => {
        onSuccess?.();
      }, 100);
    },
  });
};
